import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './Login.css';

const Login = () => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ login: '', password: '', server: '' });

  // Blocking overlay message when persistence is unavailable (required)
  const [storageBlockingMsg, setStorageBlockingMsg] = useState('');

  const navigate = useNavigate();
  const { state } = useLocation();

  // Autofocus ref
  const loginInputRef = useRef(null);
  useEffect(() => {
    loginInputRef.current?.focus({ preventScroll: true });
  }, []);

  // Helper: check if we truly have persistent storage (not the shim)
  const hasRealLocalStorage = () => {
    try {
      // The index.html polyfill sets this flag before the bundle loads
      if (typeof window !== 'undefined' && typeof window.__LOCALSTORAGE_SHIM__ === 'boolean') {
        return window.__LOCALSTORAGE_SHIM__ === false;
      }
      // Fallback probe
      const k = '__e3pl_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch {
      return false;
    }
  };

  // On mount, block immediately if persistence is unavailable
  useEffect(() => {
    if (!hasRealLocalStorage()) {
      setStorageBlockingMsg(
        'Local storage is disabled or blocked in your browser. This application requires persistent local storage to function. Please enable site storage and reload.'
      );
    } else {
      setStorageBlockingMsg('');
    }
  }, []);

  // Function to apply theme colors dynamically (persists only when storage is real)
  const applyTheme = (themeData) => {
    const defaults = {
      weak_primary_color: '#ffd0b3',
      strong_primary_color: '#ff6200',
      primary_color: '#ff6200',
      primary_hover: '#ff8c00',
      background_dark: '#1a1a1a',
      card_background: '#2c2c2c',
      input_background: '#3a3a3a',
      text_color: '#ffffff',
      error_color: '#ff0000',
      success_color: '#00ff00',
      even_row_color: '#ffffff',
      selected_row_color: '#e6cdb4',
      button_text_color: '#ffffff',
    };
    const theme = { ...defaults, ...themeData };

    document.documentElement.style.setProperty('--weak-primary-color', theme.weak_primary_color);
    document.documentElement.style.setProperty('--strong-primary-color', theme.strong_primary_color);
    document.documentElement.style.setProperty('--primary-color', theme.primary_color);
    document.documentElement.style.setProperty('--primary-hover', theme.primary_hover);
    document.documentElement.style.setProperty('--background-dark', theme.background_dark);
    document.documentElement.style.setProperty('--card-background', theme.card_background);
    document.documentElement.style.setProperty('--input-background', theme.input_background);
    document.documentElement.style.setProperty('--text-color', theme.text_color);
    document.documentElement.style.setProperty('--error-color', theme.error_color);
    document.documentElement.style.setProperty('--success-color', theme.success_color);
    document.documentElement.style.setProperty('--evenrow-color', theme.evenrow_color);
    document.documentElement.style.setProperty('--selected-row-color', theme.selected_row_color);
    document.documentElement.style.setProperty('--button-text-color', theme.button_text_color);

    try {
      if (!hasRealLocalStorage()) {
        setStorageBlockingMsg(
          'Local storage is disabled or blocked in your browser. This application requires persistent local storage to function. Please enable site storage and reload.'
        );
        return;
      }
      localStorage.setItem('themeData', JSON.stringify(themeData || {}));
    } catch (err) {
      console.error('Theme persist error:', err);
      setStorageBlockingMsg(
        'Your browser blocked saving theme settings. Please enable local storage and reload.'
      );
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { login: '', password: '', server: '' };

    if (!login) {
      newErrors.login = 'Login is required';
      isValid = false;
    } else if (login.length < 3) {
      newErrors.login = 'Login must be at least 3 characters';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // HARD BLOCK: app requires persistence
    if (!hasRealLocalStorage()) {
      setStorageBlockingMsg(
        'Local storage is disabled or blocked in your browser. This application requires persistent local storage to function. Please enable site storage and reload.'
      );
      return;
    }

    if (!validateForm()) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ login, password }),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        data = { success: false, message: 'Unexpected server response' };
      }

      if (response.ok && data?.success) {
        // Persist the logged-in user (must succeed; otherwise block)
        try {
          localStorage.setItem('user', JSON.stringify(data.user));
          if (data.token) localStorage.setItem('authToken', data.token);
          // Seed eOrders refresh interval (seconds) from user refreshtimeout (minutes)
          const rtMin = Number(data?.user?.refreshtimeout || 0);
          const rtSec = Math.max(0, Number.isFinite(rtMin) ? rtMin * 60 : 0);
          localStorage.setItem('eorders.refreshIntervalSec', String(rtSec));
          try { window.dispatchEvent(new CustomEvent('auth:login', { detail: { user: data.user } })); } catch {}
        } catch (err) {
          console.error('User persist error:', err);
          setStorageBlockingMsg(
            'Your browser blocked saving your login session. Please enable local storage and reload.'
          );
          return;
        }

        // Apply theme (may also block if persistence fails)
        applyTheme(data.themeData || {});
        if (storageBlockingMsg) return;

        // Final sanity check: ensure we’re not on a shim
        if (!hasRealLocalStorage()) {
          setStorageBlockingMsg(
            'Local storage became unavailable. Please enable site storage and reload.'
          );
          return;
        }

        // All good — proceed
        try {
          // Let dashboard know we just logged in so it can greet
          sessionStorage.setItem('justLoggedIn', '1');
        } catch {}
        navigate(state?.from?.pathname || '/dashboard', { replace: true });
      } else {
        setErrors((prev) => ({ ...prev, server: data?.message || 'Invalid login or password' }));
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors((prev) => ({ ...prev, server: 'Failed to connect to server' }));
      // Apply default theme vars for a decent look (won’t persist if blocked)
      applyTheme({});
    }
  };

  // Ensure background images resolve correctly in production under a subpath
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Choose asset base for dev/prod.
    // In production, default to backend-served public path to avoid proxy root 403s.
    const assetBase = import.meta.env.DEV
      ? '/'
      : (import.meta.env.VITE_ASSET_BASE || '/api/public/');
    const setBG = (name, file) => el.style.setProperty(name, `url('${assetBase}${file}')`);
    setBG('--bg-desktop-1x', 'warehouse_desktop_fullhd.jpeg');
    setBG('--bg-desktop-1_5x', 'warehouse_desktop_2k.jpeg');
    setBG('--bg-desktop-2x', 'warehouse_desktop_4k.jpeg');
    setBG('--bg-mobile-1x', 'warehouse_mobile_hd.jpeg');
    setBG('--bg-mobile-2x', 'warehouse_mobile_fhd.jpeg');
    // Direct fallback for environments that ignore image-set/vars
    el.style.backgroundImage = `url('${assetBase}warehouse_desktop_fullhd.jpeg')`;
  }, []);

  return (
    <div className="login-container" ref={containerRef}>
      {/* Hard-blocking, full-screen overlay when persistence is unavailable */}
      {storageBlockingMsg && (
        <div className="storage-error-overlay" role="alert" aria-live="assertive">
          <div className="storage-error-card">
            <h2>Local Storage Required</h2>
            <p>{storageBlockingMsg}</p>
            <div className="storage-error-help">
              <ul>
                <li><b>Firefox:</b> go to <code>about:config</code>, set <code>dom.storage</code> to <code>true</code>.</li>
                <li><b>Safari:</b> turn off Private Browsing and ensure “Block All Cookies” is off.</li>
                <li><b>Edge/Chrome:</b> allow cookies/site data and disable blocking extensions.</li>
              </ul>
            </div>
            <div className="storage-error-actions">
              <button
                type="button"
                className="button"
                onClick={() => {
                  // Re-check in case user changed settings
                  try {
                    const testKey = '__e3pl_recheck__';
                    window.localStorage.setItem(testKey, '1');
                    window.localStorage.removeItem(testKey);
                    // If it worked, clear the block and reload to ensure a clean state
                    setStorageBlockingMsg('');
                    window.location.reload();
                  } catch {
                    // still blocked; keep message
                  }
                }}
              >
                Recheck & Reload
              </button>
            </div>
          </div>
        </div>
      )}
      <h1 className="login-header">A Modern Warehouse Management Solution</h1>
      <div className="login-card" aria-hidden={!!storageBlockingMsg}>
        <img src={`${(import.meta.env.DEV ? '/' : (import.meta.env.VITE_ASSET_BASE || '/api/public/'))}easy3pl_logo.png`} alt="Easy3PL Logo" className="login-logo" />
        {errors.server && (
          <span className="error-message server-error" role="alert" aria-live="assertive">
            {errors.server}
          </span>
        )}
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <input
              type="text"
              id="login"
              name="username"
              ref={loginInputRef}
              autoFocus
              autoComplete="username"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              placeholder="Enter your login"
              className={`input ${errors.login ? 'input-error' : ''}`}
              aria-invalid={!!errors.login}
              aria-describedby={errors.login ? 'login-error' : undefined}
              disabled={!!storageBlockingMsg}
            />
            {errors.login && (
              <span id="login-error" className="error-message">
                {errors.login}
              </span>
            )}
          </div>
          <div className="input-group">
            <input
              type="password"
              id="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={`input ${errors.password ? 'input-error' : ''}`}
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              disabled={!!storageBlockingMsg}
            />
            {errors.password && (
              <span id="password-error" className="error-message">
                {errors.password}
              </span>
            )}
          </div>
          <button type="submit" className="login-button button" disabled={!!storageBlockingMsg}>
            Log In
          </button>
          <div className="forgot-password">
            <Link
              to="/forgot-password"
              className="forgot-password-link link"
              tabIndex={storageBlockingMsg ? -1 : 0}
            >
              Forgot Password?
            </Link>
          </div>
        </form>
      </div>

      <div className="login-disclaimer" aria-hidden={!!storageBlockingMsg}>
        <strong>Authorized Access Only.</strong> This system is for use by authorized users only.
        All activities are monitored and subject to audit. Unauthorized access or use may result
        in disciplinary action, civil liability, and/or criminal prosecution under U.S. law. By
        continuing, you agree to comply with all applicable terms, policies, and laws.
      </div>
    </div>
  );
};

export default Login;
